/**
 * Exponential backoff with full jitter for WebSocket reconnection
 * (ARCHITECTURE §7 — `WsClient` "exponential backoff + jitter").
 *
 * Real, working implementation — this is shared transport infra, not business
 * logic. Uses the AWS "full jitter" strategy: delay = random(0, min(cap, base*2^n)).
 */

export interface BackoffOptions {
  /** Base delay in ms for the first retry. Default 500ms. */
  baseMs?: number;
  /** Maximum delay in ms (the cap). Default 30_000ms. */
  maxMs?: number;
  /** Multiplier per attempt. Default 2. */
  factor?: number;
  /** Injectable RNG for deterministic tests. Default `Math.random`. */
  random?: () => number;
}

/**
 * Stateful backoff sequence. Call {@link next} for each reconnect delay and
 * {@link reset} once a connection succeeds.
 */
export class Backoff {
  private attempt = 0;
  private readonly baseMs: number;
  private readonly maxMs: number;
  private readonly factor: number;
  private readonly random: () => number;

  constructor(options: BackoffOptions = {}) {
    this.baseMs = options.baseMs ?? 500;
    this.maxMs = options.maxMs ?? 30_000;
    this.factor = options.factor ?? 2;
    this.random = options.random ?? Math.random;
  }

  /** Number of attempts since the last {@link reset}. */
  get attempts(): number {
    return this.attempt;
  }

  /** Compute the next delay (ms) with full jitter and advance the counter. */
  next(): number {
    const exp = this.baseMs * this.factor ** this.attempt;
    const capped = Math.min(this.maxMs, exp);
    this.attempt += 1;
    // Full jitter: uniformly random in [0, capped].
    return Math.floor(this.random() * capped);
  }

  /** Reset the sequence after a successful connection. */
  reset(): void {
    this.attempt = 0;
  }
}
